// ================================================
// ATRACK Analytics Dashboard Component
// client/src/pages/AtrackAnalyticsDashboard.js
// ================================================

import React, { useState } from 'react';
import { useFetch } from '../hooks/useFetch';
import '../styles/AnalyticsDashboard.css';

const AtrackAnalyticsDashboard = () => {
  const { data: dashboard, loading, error } = useFetch('/api/atrack/analytics/dashboard');
  const [activeTab, setActiveTab] = useState('overview');

  if (loading) return <div className="analytics-loading">Loading Analytics...</div>;
  if (error) return <div className="analytics-error">Error: {error}</div>;

  if (!dashboard) return null;

  const {
    summary,
    priorityType,
    overdue,
    userWorkload,
    comments,
    attachments,
    tags,
    dueDates
  } = dashboard;

  return (
    <div className="analytics-dashboard atrack-dashboard">
      <div className="dashboard-header">
        <h1>Activity & Task Analytics</h1>
        <p>Real-time insights from ATRACK IPMS</p>
      </div>

      {/* Key Metrics Cards */}
      <div className="metrics-grid">
        <MetricCard
          title="Total Activities"
          value={summary?.total_activities}
          icon="📊"
          color="blue"
        />
        <MetricCard
          title="Active Activities"
          value={summary?.active_activities}
          icon="✅"
          color="green"
        />
        <MetricCard
          title="Overdue Activities"
          value={summary?.overdue_activities}
          icon="⚠️"
          color="red"
        />
        <MetricCard
          title="Urgent Tasks"
          value={summary?.urgent_count}
          icon="🚨"
          color="orange"
        />
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users & Workload
        </button>
        <button
          className={`tab-button ${activeTab === 'engagement' ? 'active' : ''}`}
          onClick={() => setActiveTab('engagement')}
        >
          Engagement
        </button>
        <button
          className={`tab-button ${activeTab === 'documentation' ? 'active' : ''}`}
          onClick={() => setActiveTab('documentation')}
        >
          Documentation
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="section-container">
            <h2>Activity Overview</h2>

            <div className="two-column-grid">
              {/* Priority Distribution */}
              <div className="card">
                <h3>Activities by Priority & Type</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Priority</th>
                      <th>Type</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {priorityType?.map((item, i) => (
                      <tr key={i}>
                        <td>{item.priority_level}</td>
                        <td>{item.activity_type}</td>
                        <td className="count">{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Due Dates */}
              <div className="card">
                <h3>Activities by Due Date</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dueDates?.map((item, i) => (
                      <tr key={i}>
                        <td>{item.due_date_category}</td>
                        <td className="count">{item.activity_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Overdue Activities */}
            <div className="card full-width">
              <h3>Overdue Activities ({overdue?.length})</h3>
              {overdue && overdue.length > 0 ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Due Date</th>
                      <th>Days Overdue</th>
                      <th>Priority</th>
                      <th>Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overdue.slice(0, 10).map((item, i) => (
                      <tr key={i} className="overdue-row">
                        <td>{item.title}</td>
                        <td>{new Date(item.due_date).toLocaleDateString()}</td>
                        <td className="overdue">{item.days_overdue} days</td>
                        <td>
                          <span className={`priority-badge p${item.priority_id}`}>
                            {item.priority_id === 1 ? 'Urgent' : item.priority_id === 2 ? 'Regular' : 'Routine'}
                          </span>
                        </td>
                        <td>{item.comment_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="no-data">No overdue activities</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="section-container">
            <h2>User Workload & Assignments</h2>
            <div className="card">
              <h3>User Task Distribution</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Tasks Created</th>
                    <th>Active Tasks</th>
                    <th>Urgent</th>
                    <th>Regular</th>
                    <th>Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {userWorkload?.map((user, i) => (
                    <tr key={i}>
                      <td>{user.creator_user_id}</td>
                      <td className="count">{user.activities_created}</td>
                      <td className="count">{user.active_activities}</td>
                      <td className="count">{user.urgent_tasks}</td>
                      <td className="count">{user.regular_tasks}</td>
                      <td>{new Date(user.last_activity).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'engagement' && (
          <div className="section-container">
            <h2>Team Engagement Metrics</h2>
            <div className="two-column-grid">
              <div className="card">
                <h3>Comment Activity</h3>
                <p><strong>Most Commented:</strong> {comments?.[0]?.total_comments} comments</p>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Comments</th>
                      <th>Commenters</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comments?.slice(0, 5).map((item, i) => (
                      <tr key={i}>
                        <td>{item.activity_id}</td>
                        <td>{item.total_comments}</td>
                        <td>{item.unique_commenters}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="card">
                <h3>Documentation Stats</h3>
                <div className="stat-box">
                  <p>Total Attachments: <strong>{attachments?.total_attachments}</strong></p>
                  <p>With Attachments: <strong>{attachments?.activities_with_attachments}</strong></p>
                  <p>Coverage: <strong>{((attachments?.activities_with_attachments / 6) * 100).toFixed(1)}%</strong></p>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="card">
              <h3>Top Tags</h3>
              <div className="tag-container">
                {tags
                  ?.sort((a, b) => b.tagged_activities - a.tagged_activities)
                  .slice(0, 10)
                  .map((tag, i) => (
                    <span key={i} className="tag-badge">
                      {tag.tag_name} ({tag.tagged_activities})
                    </span>
                  ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'documentation' && (
          <div className="section-container">
            <h2>Documentation & Organization</h2>
            <div className="card">
              <h3>Attachment Usage</h3>
              <div className="stat-grid">
                <div className="stat-item">
                  <span className="label">Total Attachments</span>
                  <span className="value">{attachments?.total_attachments}</span>
                </div>
                <div className="stat-item">
                  <span className="label">Activities with Files</span>
                  <span className="value">{attachments?.activities_with_attachments}</span>
                </div>
                <div className="stat-item">
                  <span className="label">Coverage %</span>
                  <span className="value">{((attachments?.activities_with_attachments / 6) * 100).toFixed(1)}%</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon, color }) => (
  <div className={`metric-card ${color}`}>
    <span className="metric-icon">{icon}</span>
    <div className="metric-content">
      <p className="metric-title">{title}</p>
      <p className="metric-value">{value || 0}</p>
    </div>
  </div>
);

export default AtrackAnalyticsDashboard;
